"use client";

import { useEffect, useState } from "react";

/**
 * Reactively evaluates a CSS media query against the current viewport.
 * SSR-safe : returns `false` on the server (and on the very first client
 * render), then refreshes on mount and on every viewport change.
 *
 * Callers that branch their layout on the result should guard against the
 * initial `false` causing a one-frame flash (e.g. by disabling transitions
 * until after the first commit).
 *
 * @example
 * const isCompact = useMediaQuery("(max-width: 1023px)");
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mql.addEventListener("change", handler);

    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
